package fr.frux.feature.treeadd.wizard

import fr.frux.core.maps.Location
import fr.frux.core.media.PickedImage
import fr.frux.core.media.PreparedPhoto
import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.domain.model.DuplicateCandidate
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.TreeStatus
import fr.frux.shared.domain.photos.PhotoUpload

/** Les trois étapes de l'assistant de création, dans l'ordre. */
enum class WizardStep { PLACE, SPECIES, DETAILS }

/**
 * Ce que la route sait à l'ouverture. [guideAlreadyRead] : l'aide « Que puis-je recenser ? »
 * vient d'être lue depuis la carte, l'introduction ne se remontre pas.
 */
data class TreeWizardStart(
    val guideAlreadyRead: Boolean = false,
)

enum class SpeciesGridKind { AROUND, SEASON_FALLBACK, SEARCH, NO_RESULT }

/**
 * [count] : arbres de cette espèce autour de la position, `null` si non pertinent. [match] : portion
 * du nom à surligner.
 */
data class SpeciesTileItem(
    val species: Species,
    val count: Int?,
    val match: IntRange? = null,
)

data class SpeciesGrid(
    val kind: SpeciesGridKind,
    val tiles: List<SpeciesTileItem>,
)

enum class ReferentialLoad { Loading, Loaded, Failed }

data class SuggestionUi(
    val dialogVisible: Boolean = false,
    val sending: Boolean = false,
    val sent: Boolean = false,
    val error: AddTreeSuggestionError? = null,
)

data class SpeciesStepState(
    val referential: ReferentialLoad = ReferentialLoad.Loading,
    val query: String = "",
    val grid: SpeciesGrid = SpeciesGrid(SpeciesGridKind.AROUND, emptyList()),
    val selected: Species? = null,
    val certainty: Certainty = Certainty.PROBABLE,
    val duplicate: DuplicateCandidate? = null,
    val suggestion: SuggestionUi = SuggestionUi(),
) {
    val canContinue: Boolean get() = selected != null
}

const val MAX_WIZARD_PHOTOS = 10

/**
 * Photo choisie, identifiée par [id] : deux photos d'octets identiques restent deux photos. L'égalité
 * regarde aussi [prepared] (par identité) : sans cela, l'arrivée de la version préparée donnerait un
 * état égal à l'ancien et le `StateFlow` ne l'émettrait jamais.
 */
class WizardPhoto(
    val id: String,
    val image: PickedImage,
    val prepared: PreparedPhoto?,
) {
    override fun equals(other: Any?): Boolean = other is WizardPhoto && other.id == id && other.prepared === prepared

    override fun hashCode(): Int = id.hashCode()
}

data class DetailsStepState(
    val status: TreeStatus? = null,
    val photos: List<WizardPhoto> = emptyList(),
    val note: String = "",
) {
    val canAddPhoto: Boolean get() = photos.size < MAX_WIZARD_PHOTOS
}

sealed interface WizardSubmission {
    data object Idle : WizardSubmission

    data object Creating : WizardSubmission

    data class Uploading(
        val tree: Tree,
        val uploads: List<PhotoUpload>,
    ) : WizardSubmission
}

sealed interface TreeWizardEvent {
    data class Created(
        val tree: Tree,
        val photoUploadFailed: Boolean,
        val firstContribution: Boolean,
    ) : TreeWizardEvent

    data class ExistingTreeSelected(
        val treeId: Long,
    ) : TreeWizardEvent

    /** « Un lieu » à l'étape 1 : composeApp ouvre la création de lieu sur [position]. */
    data class SiteChosen(
        val position: Location,
    ) : TreeWizardEvent

    data object Exited : TreeWizardEvent
}

/** Ce que la personne perdrait en quittant, pour la phrase de confirmation. */
data class LostInputs(
    val species: Boolean,
    val status: Boolean,
    val photoCount: Int,
    val note: Boolean,
) {
    val isEmpty: Boolean get() = !species && !status && photoCount == 0 && !note
}

data class TreeWizardState(
    val step: WizardStep = WizardStep.PLACE,
    val position: Location? = null,
    val introVisible: Boolean = false,
    val species: SpeciesStepState = SpeciesStepState(),
    val details: DetailsStepState = DetailsStepState(),
    val submission: WizardSubmission = WizardSubmission.Idle,
    val submitFailed: Boolean = false,
    val exitPrompt: LostInputs? = null,
)

fun TreeWizardState.lostInputs(): LostInputs =
    LostInputs(
        species = species.selected != null,
        status = details.status != null,
        photoCount = details.photos.size,
        note = details.note.isNotBlank(),
    )

/**
 * Échec de l'envoi d'une suggestion d'espèce (#233 review) : [Quota] distingue le 429 métier
 * (`SUGGESTION_QUOTA_EXCEEDED`) d'une panne réseau ordinaire. Le conseil « vérifie ta connexion »
 * serait faux pour un quota, la saisie n'y est pour rien.
 */
enum class AddTreeSuggestionError {
    Network,
    Quota,
}
